import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './Documentation.css';

function Documentation() {
  const navigate = useNavigate();
  
  return (
    <div className="documentation">
      <div className="documentation-container">
        <button onClick={() => navigate(-1)} className="btn-back">
          ← Zurück
        </button>
        <h1>GarageDesk Dokumentation</h1>
        <p className="documentation-intro">
          Willkommen in der vollständigen Dokumentation von GarageDesk. Diese Anleitung erklärt alle Funktionen, 
          Workflows und Features der Anwendung.
        </p>

        <nav className="documentation-nav">
          <h2>Inhaltsverzeichnis</h2>
          <ul>
            <li><a href="#getting-started">Erste Schritte</a></li>
            <li><a href="#authentication">Authentifizierung & Benutzerverwaltung</a></li>
            <li><a href="#customers">Kundenverwaltung</a></li>
            <li><a href="#cars">Autoverwaltung</a></li>
            <li><a href="#repairs">Reparaturverwaltung</a></li>
            <li><a href="#invoices">Rechnungsverwaltung</a></li>
            <li><a href="#service-templates">Service-Templates</a></li>
            <li><a href="#inventory">Inventarverwaltung</a></li>
            <li><a href="#purchases">Einkäufe</a></li>
            <li><a href="#fleet">Flottenverwaltung</a></li>
            <li><a href="#locations">Werkstattorte</a></li>
            <li><a href="#team">Team & Rollen</a></li>
            <li><a href="#appointments">Termine</a></li>
            <li><a href="#maintenance">Wartungspläne</a></li>
            <li><a href="#reports">Berichte & KPIs</a></li>
            <li><a href="#customer-portal">Kundenportal</a></li>
            <li><a href="#admin">Admin-Dashboard</a></li>
            <li><a href="#settings">Einstellungen</a></li>
            <li><a href="#backup">Sicherungen</a></li>
            <li><a href="#datenschutz">Datenschutz & Datensicherheit</a></li>
          </ul>
        </nav>

        <section id="getting-started" className="doc-section">
          <h2>Erste Schritte</h2>
          
          <h3>Registrierung</h3>
          <p>
            Beim ersten Besuch der Anwendung können Sie sich als neuer Tenant (Werkstatt) registrieren. 
            Klicken Sie auf der Login-Seite auf den Tab "Registrieren" und füllen Sie das Formular aus:
          </p>
          <ul>
            <li><strong>Werkstattname:</strong> Der Name Ihrer Werkstatt</li>
            <li><strong>E-Mail:</strong> Ihre E-Mail-Adresse (wird für die Bestätigung benötigt)</li>
            <li><strong>Benutzername:</strong> Ihr gewünschter Benutzername</li>
            <li><strong>Passwort:</strong> Ein sicheres Passwort</li>
            <li><strong>Passwort bestätigen:</strong> Wiederholung des Passworts</li>
            <li><strong>Steuersystem:</strong> Wählen Sie zwischen Deutschland (DE) oder Schweiz (CH)</li>
          </ul>
          <p>
            Nach der Registrierung erhalten Sie eine E-Mail zur Bestätigung Ihrer E-Mail-Adresse. 
            Klicken Sie auf den Link in der E-Mail, um Ihr Konto zu aktivieren.
          </p>

          <h3>Anmeldung</h3>
          <p>
            Nach der erfolgreichen Registrierung und E-Mail-Bestätigung können Sie sich mit Ihrem 
            Benutzernamen und Passwort anmelden. Bei der ersten Anmeldung müssen Sie Ihr Passwort ändern.
          </p>

          <h3>Navigation</h3>
          <p>
            Die Anwendung ist in mehrere Hauptbereiche unterteilt:
          </p>
          <ul>
            <li><strong>Stammdaten:</strong> <Link to="/customers">Kunden</Link> und <Link to="/cars">Autos</Link></li>
            <li><strong>Aufträge:</strong> <Link to="/repairs">Reparaturen</Link>, <Link to="/invoices">Rechnungen</Link> und <Link to="/service-templates">Service-Templates</Link></li>
            <li><strong>Werkstatt:</strong> Team, Standorte, Inventar, Einkäufe, Termine, Berichte, Einstellungen</li>
            <li><strong>Fahrzeuge:</strong> <Link to="/fleet/inventory">Bestand</Link> und <Link to="/fleet/sales">Verkäufe</Link></li>
          </ul>
        </section>

        <section id="authentication" className="doc-section">
          <h2>Authentifizierung & Benutzerverwaltung</h2>
          
          <h3>Rollen</h3>
          <p>GarageDesk unterstützt verschiedene Benutzerrollen:</p>
          <ul>
            <li><strong>Admin:</strong> Vollzugriff auf alle Tenants und das Admin-Dashboard</li>
            <li><strong>TenantAdmin:</strong> Vollzugriff auf alle Funktionen innerhalb eines Tenants</li>
            <li><strong>User:</strong> Standard-Benutzer mit eingeschränkten Rechten (abhängig von zugewiesenen Rollen)</li>
          </ul>

          <h3>Passwort ändern</h3>
          <p>
            Sie können Ihr Passwort jederzeit über <Link to="/change-password">Einstellungen → Passwort ändern</Link> ändern.
          </p>

          <h3>Login-Sperre</h3>
          <p>
            Nach mehreren fehlgeschlagenen Login-Versuchen wird Ihr Konto temporär gesperrt, um Brute-Force-Angriffe zu verhindern.
          </p>
        </section>

        <section id="customers" className="doc-section">
          <h2>Kundenverwaltung</h2>
          
          <h3>Kunden anlegen</h3>
          <p>
            Navigieren Sie zu <Link to="/customers">Stammdaten → Kunden</Link> und klicken Sie auf "Neuer Kunde". 
            Füllen Sie die folgenden Informationen aus:
          </p>
          <ul>
            <li><strong>Persönliche Daten:</strong> Vorname, Nachname, Geburtsdatum</li>
            <li><strong>Kontaktdaten:</strong> E-Mail, Telefon, Mobil</li>
            <li><strong>Adresse:</strong> Straße, PLZ, Ort, Land</li>
            <li><strong>Zusätzliche Informationen:</strong> Notizen, Tags</li>
          </ul>

          <h3>Kundendetails</h3>
          <p>
            Auf der <Link to="/customers">Kundendetailseite</Link> sehen Sie:
          </p>
          <ul>
            <li>Alle zugeordneten Fahrzeuge</li>
            <li>Reparaturhistorie</li>
            <li>Rechnungshistorie</li>
            <li>Termine</li>
            <li>Kundenportal-Zugang (QR-Code auf Rechnungen)</li>
          </ul>

          <h3>Kunden bearbeiten</h3>
          <p>
            Klicken Sie auf einen Kunden in der Liste, um die Details zu öffnen. Dort können Sie alle 
            Informationen bearbeiten und speichern.
          </p>
        </section>

        <section id="cars" className="doc-section">
          <h2>Autoverwaltung</h2>
          
          <h3>Auto anlegen</h3>
          <p>
            Navigieren Sie zu <Link to="/cars">Stammdaten → Autos</Link> und klicken Sie auf "Neues Auto". 
            Ein Auto muss immer einem Kunden zugeordnet sein.
          </p>
          <p>Erforderliche Informationen:</p>
          <ul>
            <li><strong>Fahrzeugdaten:</strong> Marke, Modell, Baujahr, Fahrgestellnummer (VIN), Kennzeichen</li>
            <li><strong>Technische Daten:</strong> Kraftstoffart, Getriebeart, Kilometerstand, Motorleistung</li>
            <li><strong>Kunde:</strong> Zugeordneter Kunde</li>
            <li><strong>Zusätzlich:</strong> Unfallfahrzeug (Checkbox), Beschreibung, Bilder</li>
          </ul>


          <h3>mobile.de Link</h3>
          <p>
            Auf der Autodetailseite finden Sie einen Link "Auf mobile.de suchen", der automatisch eine 
            Suche nach ähnlichen Fahrzeugen auf mobile.de öffnet, basierend auf den Fahrzeugdaten.
          </p>

          <h3>Bekannte Defekte</h3>
          <p>
            Sie können für jedes Auto eine Liste bekannter Defekte verwalten. Diese werden auch beim 
            Verkauf eines Fahrzeugs aus der Flotte an einen Kunden übertragen.
          </p>

          <h3>Bekannte Unfälle</h3>
          <p>
            Wenn ein Auto als "Unfallfahrzeug" markiert ist, können Sie eine Liste bekannter Unfälle 
            mit Datum, Beschreibung und Schadenshöhe verwalten.
          </p>

          <h3>Fahrzeughistorie</h3>
          <p>
            Auf der Autodetailseite sehen Sie:
          </p>
          <ul>
            <li>Alle Reparaturen für dieses Auto</li>
            <li>Rechnungshistorie</li>
            <li>Kilometerstand-Verlauf</li>
            <li>Wartungspläne</li>
          </ul>
        </section>

        <section id="repairs" className="doc-section">
          <h2>Reparaturverwaltung</h2>
          
          <h3>Reparatur anlegen</h3>
          <p>
            Navigieren Sie zu <Link to="/repairs">Aufträge → Reparaturen</Link> und klicken Sie auf "Neue Reparatur".
          </p>
          <p>Schritte:</p>
          <ol>
            <li><strong>Auto auswählen:</strong> Wählen Sie das zu reparierende Fahrzeug</li>
            <li><strong>Service-Templates (optional):</strong> Wählen Sie vordefinierte Service-Templates aus, um Materialien und Arbeitszeiten automatisch hinzuzufügen</li>
            <li><strong>Materialien hinzufügen:</strong>
              <ul>
                <li>Beschreibung, Menge, Einzelpreis</li>
                <li>Materialien können aus dem <Link to="#inventory">Inventar</Link> ausgewählt werden</li>
              </ul>
            </li>
            <li><strong>Arbeitszeiten hinzufügen:</strong>
              <ul>
                <li>Beschreibung, Stunden, Stundensatz (aus <Link to="#hourly-rates">Stundensätzen</Link>)</li>
              </ul>
            </li>
            <li><strong>Status setzen:</strong> Offen, In Arbeit, Abgeschlossen, Storniert</li>
            <li><strong>Zuweisung:</strong> Optional einem Teammitglied zuweisen</li>
            <li><strong>Kilometerstand:</strong> Wird automatisch im Auto aktualisiert, wenn höher als der aktuelle Wert</li>
          </ol>

          <h3>Reparatur bearbeiten</h3>
          <p>
            Klicken Sie auf eine Reparatur in der Liste, um Details zu öffnen. Dort können Sie:
          </p>
          <ul>
            <li>Materialien und Arbeitszeiten hinzufügen/bearbeiten/löschen</li>
            <li>Status ändern</li>
            <li>Interne Notizen hinzufügen</li>
            <li>Eine Rechnung erstellen (siehe <Link to="#invoices">Rechnungsverwaltung</Link>)</li>
          </ul>

          <h3>Reparaturstatus</h3>
          <ul>
            <li><strong>Offen:</strong> Reparatur wurde angelegt, aber noch nicht begonnen</li>
            <li><strong>In Arbeit:</strong> Reparatur wird aktuell durchgeführt</li>
            <li><strong>Abgeschlossen:</strong> Reparatur ist fertiggestellt</li>
            <li><strong>Storniert:</strong> Reparatur wurde abgebrochen</li>
          </ul>

          <h3>Rechnung erstellen</h3>
          <p>
            Aus einer abgeschlossenen Reparatur können Sie direkt eine Rechnung erstellen. 
            Siehe <Link to="#invoices">Rechnungsverwaltung</Link> für Details.
          </p>
        </section>

        <section id="invoices" className="doc-section">
          <h2>Rechnungsverwaltung</h2>
          
          <h3>Rechnung aus Reparatur erstellen</h3>
          <p>
            Navigieren Sie zu <Link to="/repairs">Reparaturen</Link>, öffnen Sie eine abgeschlossene Reparatur 
            und klicken Sie auf "Rechnung erstellen". Die Rechnung wird automatisch mit allen Materialien und 
            Arbeitszeiten aus der Reparatur befüllt.
          </p>

          <h3>Rechnung manuell erstellen</h3>
          <p>
            Sie können auch eine Rechnung manuell in <Link to="/invoices">Aufträge → Rechnungen</Link> erstellen, 
            ohne eine Reparatur als Basis zu verwenden.
          </p>

          <h3>Rechnungsdetails</h3>
          <p>
            Auf der Rechnungsdetailseite können Sie:
          </p>
          <ul>
            <li>Rechnungspositionen bearbeiten</li>
            <li>Status ändern (Offen, Bezahlt, Überfällig, Storniert)</li>
            <li>Fälligkeitsdatum setzen</li>
            <li>PDF herunterladen</li>
            <li>QR-Code für Kundenportal anzeigen</li>
          </ul>

          <h3>Steuersysteme</h3>
          <p>
            GarageDesk unterstützt verschiedene Steuersysteme:
          </p>
          <ul>
            <li><strong>Deutschland (DE):</strong> 19% MwSt. (Standard), 7% MwSt. (ermäßigt), Kleinunternehmerregelung</li>
            <li><strong>Schweiz (CH):</strong> 7.7% MwSt. (Standard), 3.7% MwSt. (ermäßigt), 2.5% MwSt. (Sonderfall)</li>
          </ul>
          <p>
            Das Steuersystem wird beim Anlegen des Tenants festgelegt und kann in den <Link to="#settings">Einstellungen</Link> geändert werden.
          </p>

          <h3>Rechnungsnummern</h3>
          <p>
            Rechnungsnummern werden automatisch generiert im Format:
          </p>
          <ul>
            <li><strong>DE:</strong> RE-000001, RE-000002, ...</li>
            <li><strong>CH:</strong> CH-000001, CH-000002, ...</li>
          </ul>

          <h3>Kundenportal-Zugang</h3>
          <p>
            Wenn das Kundenportal aktiviert ist, wird auf jeder Rechnung ein QR-Code angezeigt, mit dem 
            der Kunde auf sein Self-Service-Portal zugreifen kann. Siehe <Link to="#customer-portal">Kundenportal</Link>.
          </p>
        </section>

        <section id="service-templates" className="doc-section">
          <h2>Service-Templates</h2>
          
          <h3>Was sind Service-Templates?</h3>
          <p>
            Service-Templates sind vordefinierte Reparaturvorlagen, die häufig durchgeführte Services enthalten. 
            Sie sparen Zeit beim Anlegen von Reparaturen, da Materialien und Arbeitszeiten automatisch hinzugefügt werden.
          </p>

          <h3>Template erstellen</h3>
          <p>
            Navigieren Sie zu <Link to="/service-templates">Aufträge → Service-Templates</Link> und klicken Sie auf "Neues Template".
          </p>
          <p>Ein Template enthält:</p>
          <ul>
            <li><strong>Name:</strong> z.B. "Ölwechsel", "Inspektion", "Bremsenwechsel"</li>
            <li><strong>Materialien:</strong> Liste von Materialien mit Mengen und Preisen</li>
            <li><strong>Arbeitszeiten:</strong> Liste von Arbeitszeiten mit Stunden und Stundensätzen</li>
            <li><strong>Beschreibung:</strong> Optionale Beschreibung des Services</li>
          </ul>

          <h3>Template verwenden</h3>
          <p>
            Beim Anlegen einer neuen Reparatur können Sie ein oder mehrere Service-Templates auswählen. 
            Die Materialien und Arbeitszeiten werden automatisch zur Reparatur hinzugefügt und können 
            anschließend noch angepasst werden.
          </p>
        </section>

        <section id="inventory" className="doc-section">
          <h2>Inventarverwaltung</h2>
          
          <h3>Inventar-Item anlegen</h3>
          <p>
            Navigieren Sie zu <Link to="/inventory">Werkstatt → Inventar</Link> und klicken Sie auf "Neues Item".
          </p>
          <p>Ein Inventar-Item enthält:</p>
          <ul>
            <li><strong>Bezeichnung:</strong> Name des Teils</li>
            <li><strong>Teilenummer:</strong> Herstellerteilnummer (wichtig für externe Links)</li>
            <li><strong>Kategorie:</strong> z.B. "Motor", "Bremsen", "Elektrik"</li>
            <li><strong>Lagerbestand:</strong> Aktuelle Menge auf Lager</li>
            <li><strong>Mindestbestand:</strong> Warnung bei Unterschreitung</li>
            <li><strong>Einkaufspreis:</strong> Preis pro Einheit</li>
            <li><strong>Verkaufspreis:</strong> Preis für Kunden</li>
            <li><strong>Lagerort:</strong> Wo das Teil gelagert wird</li>
            <li><strong>Lieferant:</strong> Optional</li>
          </ul>

          <h3>Externe Links</h3>
          <p>
            Wenn eine Teilenummer vorhanden ist, werden automatisch Links zu externen Anbietern angezeigt:
          </p>
          <ul>
            <li><strong>AutoDoc:</strong> Direkter Link zur Suche auf autodoc.de</li>
            <li><strong>eBay:</strong> Direkter Link zur Suche auf ebay.de (Kategorie: Autoteile)</li>
          </ul>
          <p>
            Diese Links helfen beim Preisvergleich und bei der Beschaffung von Ersatzteilen.
          </p>

          <h3>Lagerbestand verwalten</h3>
          <p>
            Der Lagerbestand wird automatisch aktualisiert, wenn:
          </p>
          <ul>
            <li>Ein Einkauf erfasst wird (siehe <Link to="#purchases">Einkäufe</Link>)</li>
            <li>Materialien in einer Reparatur verwendet werden (optional, manuell)</li>
          </ul>

          <h3>Niedriger Bestand</h3>
          <p>
            Wenn der Lagerbestand unter den Mindestbestand fällt, wird das Item in den <Link to="#reports">Berichten</Link> 
            als "Niedriger Bestand" angezeigt.
          </p>
        </section>

        <section id="purchases" className="doc-section">
          <h2>Einkäufe</h2>
          
          <h3>Einkauf anlegen</h3>
          <p>
            Navigieren Sie zu <Link to="/purchases">Werkstatt → Einkäufe</Link> und klicken Sie auf "Neuer Einkauf".
          </p>
          <p>Ein Einkauf enthält:</p>
          <ul>
            <li><strong>Lieferant:</strong> Name des Lieferanten</li>
            <li><strong>Datum:</strong> Einkaufsdatum</li>
            <li><strong>Rechnungsnummer:</strong> Rechnungsnummer vom Lieferanten</li>
            <li><strong>Positionen:</strong> Liste von gekauften Items
              <ul>
                <li>Beschreibung</li>
                <li>Menge</li>
                <li>Einzelpreis (mit Währungssymbol)</li>
                <li>Optional: Verknüpfung mit Inventar-Item</li>
              </ul>
            </li>
            <li><strong>Gesamtbetrag:</strong> Wird automatisch berechnet</li>
          </ul>

          <h3>Einkauf mit Inventar verknüpfen</h3>
          <p>
            Wenn Sie ein Inventar-Item auswählen, wird der Lagerbestand automatisch erhöht, wenn der Einkauf gespeichert wird.
          </p>

          <h3>Einkäufe in Berichten</h3>
          <p>
            Alle Einkäufe werden in den <Link to="#reports">Berichten</Link> erfasst und können für die 
            Steuererklärung und Kostenanalyse verwendet werden.
          </p>
        </section>

        <section id="fleet" className="doc-section">
          <h2>Flottenverwaltung</h2>
          
          <h3>Übersicht</h3>
          <p>
            Die Flottenverwaltung ermöglicht es Ihnen, eigene Fahrzeuge zu verwalten, die Sie ankaufen, 
            verkaufen oder als Werkstattfahrzeuge nutzen.
          </p>

          <h3>Fahrzeug hinzufügen</h3>
          <p>
            Navigieren Sie zu <Link to="/fleet/inventory">Fahrzeuge → Bestand</Link> und klicken Sie auf "Neues Fahrzeug".
          </p>
          <p>Ein Flottenfahrzeug enthält:</p>
          <ul>
            <li><strong>Fahrzeugdaten:</strong> Marke, Modell, Baujahr, VIN, Kennzeichen</li>
            <li><strong>Ankauf:</strong> Ankaufspreis, Ankaufsdatum</li>
            <li><strong>Marktwert:</strong> Aktueller geschätzter Marktwert</li>
            <li><strong>Status:</strong> Eigentum, Verkauft</li>
            <li><strong>Bekannte Defekte:</strong> Liste von Defekten (wie bei Kundenfahrzeugen)</li>
            <li><strong>Unfälle:</strong> Liste von Unfällen (wenn als Unfallfahrzeug markiert)</li>
          </ul>

          <h3>Fahrzeug verkaufen</h3>
          <p>
            Wenn Sie ein Fahrzeug verkaufen möchten:
          </p>
          <ol>
            <li>Öffnen Sie das Fahrzeug im <Link to="/fleet/inventory">Bestand</Link></li>
            <li>Klicken Sie auf "Verkaufen"</li>
            <li>Wählen Sie den Kunden aus, an den das Fahrzeug verkauft wird</li>
            <li>Geben Sie Verkaufspreis und Verkaufsdatum ein</li>
            <li>Das Fahrzeug wird automatisch:
              <ul>
                <li>Aus dem Bestand entfernt</li>
                <li>Dem Kunden als neues Auto zugeordnet</li>
                <li>Alle bekannten Defekte und Unfälle werden übertragen</li>
                <li>In <Link to="/fleet/sales">Verkäufe</Link> angezeigt</li>
              </ul>
            </li>
          </ol>

          <h3>Kaufvertrag PDF</h3>
          <p>
            Für verkaufte Fahrzeuge können Sie einen Kaufvertrag als PDF generieren. Klicken Sie auf 
            "Kaufvertrag PDF" bei einem verkauften Fahrzeug in <Link to="/fleet/sales">Verkäufe</Link>.
          </p>
          <p>Der Kaufvertrag enthält automatisch:</p>
          <ul>
            <li>Verkäufer-Informationen (aus Werkstattdaten)</li>
            <li>Käufer-Informationen (aus Kundendaten)</li>
            <li>Fahrzeugdaten</li>
            <li>Unfallfahrzeug-Status</li>
            <li>Bekannte Defekte</li>
            <li>Verkaufspreis (in Worten und Zahlen)</li>
            <li>Verkaufsdatum</li>
          </ul>

          <h3>Flotten-KPIs</h3>
          <p>
            In den <Link to="#reports">Berichten</Link> finden Sie Flotten-KPIs:
          </p>
          <ul>
            <li>Anzahl eigener Fahrzeuge</li>
            <li>Gesamter Ankaufswert</li>
            <li>Gesamter Marktwert</li>
            <li>Anzahl verkaufter Fahrzeuge</li>
            <li>Gesamter Verkaufswert</li>
            <li>Durchschnittlicher Ankaufspreis</li>
            <li>Durchschnittlicher Marktwert</li>
            <li>Gewinn/Verlust (Verkäufe)</li>
          </ul>
        </section>

        <section id="locations" className="doc-section">
          <h2>Werkstattorte</h2>
          
          <h3>Standort anlegen</h3>
          <p>
            Navigieren Sie zu <Link to="/locations">Werkstatt → Standorte</Link> und klicken Sie auf "Neuer Standort".
          </p>
          <p>Ein Standort enthält:</p>
          <ul>
            <li><strong>Name:</strong> z.B. "Hauptwerkstatt", "Filiale Nord"</li>
            <li><strong>Adresse:</strong> Straße, PLZ, Ort</li>
            <li><strong>Kontaktdaten:</strong> Telefon, E-Mail</li>
            <li><strong>Monatliche Miete:</strong> Optionale Mietkosten (für Berichte)</li>
            <li><strong>Öffnungszeiten:</strong> Optional</li>
          </ul>

          <h3>Standorte in Berichten</h3>
          <p>
            Die monatlichen Mietkosten aller Standorte werden in den <Link to="#reports">Berichten</Link> 
            als "Mietausgaben" erfasst.
          </p>
        </section>

        <section id="team" className="doc-section">
          <h2>Team & Rollen</h2>
          
          <h3>Teams</h3>
          <p>
            Navigieren Sie zu <Link to="/team">Werkstatt → Team</Link>, um Teams zu verwalten.
          </p>
          <p>Ein Team enthält:</p>
          <ul>
            <li><strong>Name:</strong> z.B. "Mechaniker", "Lackierer"</li>
            <li><strong>Mitglieder:</strong> Liste von Benutzern</li>
          </ul>

          <h3>Rollen</h3>
          <p>
            Navigieren Sie zu <Link to="/roles">Werkstatt → Rollen</Link>, um Rollen zu verwalten.
          </p>
          <p>Eine Rolle definiert Berechtigungen für:</p>
          <ul>
            <li>Kunden anlegen/bearbeiten/löschen</li>
            <li>Autos anlegen/bearbeiten/löschen</li>
            <li>Reparaturen anlegen/bearbeiten/löschen</li>
            <li>Rechnungen anlegen/bearbeiten/löschen</li>
            <li>Inventar verwalten</li>
            <li>Einkäufe verwalten</li>
            <li>Berichte anzeigen</li>
            <li>Einstellungen ändern</li>
          </ul>

          <h3>Benutzer zuweisen</h3>
          <p>
            In <Link to="/team">Team</Link> können Sie Benutzern Teams und Rollen zuweisen. 
            Benutzer mit eingeschränkten Rollen sehen nur die Funktionen, für die sie berechtigt sind.
          </p>

          <h3>Stundensätze</h3>
          <p>
            Navigieren Sie zu <Link to="/hourly-rates">Werkstatt → Stundensätze</Link>, um verschiedene 
            Stundensätze zu definieren (z.B. "Standard", "Express", "Nacharbeit"). Diese können dann 
            bei der Erstellung von Reparaturen verwendet werden.
          </p>
        </section>

        <section id="appointments" className="doc-section">
          <h2>Termine</h2>
          
          <h3>Termin anlegen</h3>
          <p>
            Navigieren Sie zu <Link to="/appointments">Werkstatt → Termine</Link> und klicken Sie auf "Neuer Termin".
          </p>
          <p>Ein Termin enthält:</p>
          <ul>
            <li><strong>Kunde:</strong> Zugeordneter Kunde</li>
            <li><strong>Auto:</strong> Optional, zugeordnetes Auto</li>
            <li><strong>Datum & Uhrzeit:</strong> Wann der Termin stattfindet</li>
            <li><strong>Beschreibung:</strong> Was gemacht werden soll</li>
            <li><strong>Status:</strong> Angefragt, Bestätigt, Abgeschlossen, Storniert</li>
            <li><strong>Zugewiesen an:</strong> Optional, Teammitglied</li>
          </ul>

          <h3>E-Mail-Benachrichtigungen</h3>
          <p>
            Wenn E-Mail konfiguriert ist, werden automatisch E-Mails versendet:
          </p>
          <ul>
            <li><strong>Terminanfrage:</strong> An den Kunden, wenn ein Termin angelegt wird</li>
            <li><strong>Bestätigung:</strong> An den Kunden, wenn ein Termin bestätigt wird</li>
            <li><strong>Stornierung:</strong> An den Kunden, wenn ein Termin storniert wird</li>
          </ul>

          <h3>Termine aus Kundenportal</h3>
          <p>
            Kunden können auch über das <Link to="#customer-portal">Kundenportal</Link> Termine anfragen. 
            Diese erscheinen in der Terminliste mit dem Status "Angefragt" und müssen bestätigt werden.
          </p>
        </section>

        <section id="maintenance" className="doc-section">
          <h2>Wartungspläne</h2>
          
          <h3>Wartungsplan anlegen</h3>
          <p>
            Navigieren Sie zu <Link to="/maintenance-plans">Werkstatt → Wartungspläne</Link> und klicken Sie auf "Neuer Plan".
          </p>
          <p>Ein Wartungsplan enthält:</p>
          <ul>
            <li><strong>Auto:</strong> Zugeordnetes Fahrzeug</li>
            <li><strong>Service-Template:</strong> Welcher Service durchgeführt werden soll</li>
            <li><strong>Intervall:</strong> Kilometer oder Zeit (z.B. alle 15.000 km oder alle 12 Monate)</li>
            <li><strong>Nächster Service:</strong> Wann der nächste Service fällig ist</li>
          </ul>

          <h3>Automatische Erinnerungen</h3>
          <p>
            Das System prüft regelmäßig alle Wartungspläne und erstellt automatisch Reparaturen, 
            wenn ein Service fällig ist (basierend auf Kilometerstand oder Zeit).
          </p>
        </section>

        <section id="reports" className="doc-section">
          <h2>Berichte & KPIs</h2>
          
          <h3>Übersicht</h3>
          <p>
            Navigieren Sie zu <Link to="/reports">Werkstatt → Berichte</Link>, um umfassende Berichte 
            und Key Performance Indicators (KPIs) einzusehen.
          </p>

          <h3>Umsatz-KPIs</h3>
          <ul>
            <li><strong>Monatlicher Umsatz:</strong> Diagramm mit monatlichen Umsätzen</li>
            <li><strong>Gesamtumsatz:</strong> Summe aller Rechnungen</li>
            <li><strong>Durchschnittlicher Umsatz:</strong> Pro Monat/Quartal</li>
          </ul>

          <h3>Reparatur-KPIs</h3>
          <ul>
            <li><strong>Reparaturen nach Status:</strong> Kreisdiagramm (Offen, In Arbeit, Abgeschlossen, Storniert)</li>
            <li><strong>Offene Reparaturen:</strong> Anzahl</li>
            <li><strong>In Arbeit:</strong> Anzahl</li>
            <li><strong>Abgeschlossen:</strong> Anzahl</li>
          </ul>

          <h3>Rechnungs-KPIs</h3>
          <ul>
            <li><strong>Rechnungen nach Status:</strong> Kreisdiagramm (Offen, Bezahlt, Überfällig, Storniert)</li>
            <li><strong>Offene Rechnungen:</strong> Anzahl und Gesamtbetrag</li>
            <li><strong>Bezahlte Rechnungen:</strong> Anzahl und Gesamtbetrag</li>
            <li><strong>Überfällige Rechnungen:</strong> Anzahl und Gesamtbetrag</li>
          </ul>

          <h3>Lager-KPIs</h3>
          <ul>
            <li><strong>Niedriger Bestand:</strong> Anzahl Items unter Mindestbestand</li>
            <li><strong>Gesamtwert Lager:</strong> Summe aller Lagerbestände</li>
            <li><strong>Anschaffungen:</strong> Anzahl und Gesamtbetrag</li>
          </ul>

          <h3>Flotten-KPIs</h3>
          <ul>
            <li><strong>Eigene Fahrzeuge:</strong> Anzahl</li>
            <li><strong>Gesamter Ankaufswert:</strong> Summe</li>
            <li><strong>Gesamter Marktwert:</strong> Summe</li>
            <li><strong>Verkaufte Fahrzeuge:</strong> Anzahl</li>
            <li><strong>Gesamter Verkaufswert:</strong> Summe</li>
            <li><strong>Durchschnittlicher Ankaufspreis:</strong> Durchschnitt</li>
            <li><strong>Durchschnittlicher Marktwert:</strong> Durchschnitt</li>
            <li><strong>Gewinn/Verlust (Verkäufe):</strong> Differenz zwischen Verkaufspreis und Ankaufspreis</li>
          </ul>

          <h3>Steuerbericht</h3>
          <p>
            Sie können einen Steuerbericht als PDF generieren, der alle Einnahmen und Ausgaben für 
            einen bestimmten Zeitraum enthält. Wählen Sie das Jahr aus und klicken Sie auf "Steuerbericht PDF".
          </p>
        </section>

        <section id="customer-portal" className="doc-section">
          <h2>Kundenportal</h2>
          
          <h3>Aktivierung</h3>
          <p>
            Das Kundenportal kann in den <Link to="#settings">Einstellungen</Link> aktiviert werden. 
            Wenn aktiviert, wird auf jeder Rechnung ein QR-Code angezeigt.
          </p>

          <h3>Zugang für Kunden</h3>
          <p>
            Kunden können den QR-Code auf ihrer Rechnung scannen oder den Link manuell eingeben, 
            um auf ihr Self-Service-Portal zuzugreifen.
          </p>

          <h3>Funktionen im Kundenportal</h3>
          <p>
            Kunden können im Portal:
          </p>
          <ul>
            <li><strong>Rechnungen einsehen:</strong> Alle Rechnungen für ihre Fahrzeuge</li>
            <li><strong>Rechnungen herunterladen:</strong> PDF-Download</li>
            <li><strong>Termine anfragen:</strong> Neue Termine für ihre Fahrzeuge anfragen</li>
            <li><strong>Termine einsehen:</strong> Alle ihre Termine sehen</li>
          </ul>

          <h3>Sicherheit</h3>
          <p>
            Jeder Zugangscode ist eindeutig und nur für eine spezifische Rechnung gültig. 
            Kunden können nur auf ihre eigenen Daten zugreifen.
          </p>
        </section>

        <section id="admin" className="doc-section">
          <h2>Admin-Dashboard</h2>
          
          <h3>Zugang</h3>
          <p>
            Das Admin-Dashboard ist nur für Benutzer mit der Rolle "Admin" zugänglich. 
            Es ist unter <code>/admin</code> erreichbar oder über eine separate URL, 
            wenn <code>ADMIN_DASHBOARD_URL</code> konfiguriert ist.
          </p>

          <h3>Tenant-Verwaltung</h3>
          <p>
            Im Admin-Dashboard können Sie:
          </p>
          <ul>
            <li>Alle Tenants (Werkstätten) einsehen</li>
            <li>Neue Tenants anlegen</li>
            <li>Tenants bearbeiten</li>
            <li>Tenants löschen</li>
          </ul>

          <h3>Benutzer-Verwaltung</h3>
          <p>
            Im Admin-Dashboard können Sie:
          </p>
          <ul>
            <li>Alle Benutzer einsehen (über alle Tenants hinweg)</li>
            <li>Neue Benutzer anlegen</li>
            <li>Benutzer bearbeiten</li>
            <li>Benutzer löschen</li>
            <li>Benutzern Tenants zuweisen</li>
          </ul>
        </section>

        <section id="settings" className="doc-section">
          <h2>Einstellungen</h2>
          
          <h3>Werkstattdaten</h3>
          <p>
            Navigieren Sie zu <Link to="/settings">Werkstatt → Einstellungen</Link>, um Ihre Werkstattdaten zu bearbeiten:
          </p>
          <ul>
            <li><strong>Name:</strong> Werkstattname</li>
            <li><strong>Adresse:</strong> Straße, PLZ, Ort</li>
            <li><strong>Kontaktdaten:</strong> Telefon, E-Mail, Website</li>
            <li><strong>Steuersystem:</strong> Deutschland oder Schweiz</li>
            <li><strong>Kleinunternehmer:</strong> Aktivieren, wenn Sie die Kleinunternehmerregelung nutzen (nur DE)</li>
            <li><strong>Umsatzsteuer-ID:</strong> Optional</li>
            <li><strong>Kundenportal:</strong> Aktivieren/Deaktivieren</li>
          </ul>

          <h3>Passwort ändern</h3>
          <p>
            Sie können Ihr Passwort über <Link to="/change-password">Einstellungen → Passwort ändern</Link> ändern.
          </p>
        </section>

        <section id="backup" className="doc-section">
          <h2>Sicherungen</h2>
          
          <h3>Backup erstellen</h3>
          <p>
            Navigieren Sie zu <Link to="/settings">Einstellungen</Link> und klicken Sie auf "Sicherung exportieren". 
            Es wird eine <code>.tar.gz</code> Datei heruntergeladen, die alle Ihre Daten enthält.
          </p>

          <h3>Backup wiederherstellen</h3>
          <p>
            Navigieren Sie zu <Link to="/settings">Einstellungen</Link> und klicken Sie auf "Sicherung importieren". 
            Wählen Sie eine zuvor exportierte <code>.tar.gz</code> Datei aus. 
            <strong>Warnung:</strong> Dies überschreibt alle aktuellen Daten!
          </p>

          <h3>Automatische Snapshots</h3>
          <p>
            Wenn Azure Blob Storage konfiguriert ist, werden automatisch Snapshots erstellt und in Azure gespeichert. 
            Sie können diese Snapshots in den Einstellungen einsehen und wiederherstellen.
          </p>

          <h3>Datenmigration</h3>
          <p>
            Beim Importieren von Backups werden automatisch Datenmigrationen durchgeführt, falls die Backup-Daten 
            eine ältere Schema-Version haben. Dies stellt sicher, dass alle Daten kompatibel sind.
          </p>
        </section>

        <section className="doc-section">
          <h2>Häufige Fragen (FAQ)</h2>
          
          <h3>Wie erstelle ich eine Rechnung aus einer Reparatur?</h3>
          <p>
            Öffnen Sie die Reparatur in <Link to="/repairs">Reparaturen</Link> und klicken Sie auf "Rechnung erstellen". 
            Die Rechnung wird automatisch mit allen Materialien und Arbeitszeiten befüllt.
          </p>

          <h3>Wie aktiviere ich das Kundenportal?</h3>
          <p>
            Gehen Sie zu <Link to="/settings">Einstellungen</Link> und aktivieren Sie "Kundenportal aktivieren". 
            Danach erscheint auf allen neuen Rechnungen ein QR-Code.
          </p>

          <h3>Wie füge ich Materialien aus dem Inventar zu einer Reparatur hinzu?</h3>
          <p>
            Beim Anlegen oder Bearbeiten einer Reparatur können Sie bei jedem Material ein Inventar-Item auswählen. 
            Der Preis wird automatisch übernommen.
          </p>

          <h3>Wie verkaufe ich ein Fahrzeug aus der Flotte?</h3>
          <p>
            Öffnen Sie das Fahrzeug in <Link to="/fleet/inventory">Fahrzeuge → Bestand</Link> und klicken Sie auf "Verkaufen". 
            Wählen Sie den Kunden aus und geben Sie Verkaufspreis und -datum ein.
          </p>

          <h3>Wie erstelle ich einen Steuerbericht?</h3>
          <p>
            Gehen Sie zu <Link to="/reports">Berichte</Link>, wählen Sie das Jahr aus und klicken Sie auf "Steuerbericht PDF".
          </p>

          <h3>Wie funktionieren Service-Templates?</h3>
          <p>
            Service-Templates sind vordefinierte Reparaturvorlagen. Beim Anlegen einer Reparatur können Sie 
            ein oder mehrere Templates auswählen, und die Materialien und Arbeitszeiten werden automatisch hinzugefügt.
          </p>
        </section>

        <section id="datenschutz" className="doc-section">
          <h2>Datenschutz & Datensicherheit</h2>
          
          <h3>Verantwortungsvoller Umgang mit Daten</h3>
          <p>
            Der Schutz Ihrer Daten und der Daten Ihrer Kunden hat für uns höchste Priorität. 
            GarageDesk wurde von Grund auf mit Fokus auf Datenschutz und Datensicherheit entwickelt.
          </p>

          <h3>Technische Sicherheitsmaßnahmen</h3>
          <ul>
            <li><strong>Verschlüsselung:</strong> Alle Datenübertragungen erfolgen verschlüsselt über SSL/TLS.</li>
            <li><strong>Multi-Tenancy:</strong> Jeder Mandant hat eine separate Datenbank. Daten werden strikt getrennt.</li>
            <li><strong>Authentifizierung:</strong> Sichere Passwort-Authentifizierung mit verschlüsselter Speicherung.</li>
            <li><strong>Zugriffskontrolle:</strong> Rollenbasierte Zugriffskontrolle (RBAC) sorgt dafür, dass nur berechtigte Personen auf Daten zugreifen können.</li>
            <li><strong>Regelmäßige Backups:</strong> Automatische, verschlüsselte Backups gewährleisten die Verfügbarkeit Ihrer Daten.</li>
          </ul>

          <h3>Datenschutz-Grundsätze</h3>
          <ul>
            <li><strong>Datenminimierung:</strong> Wir erheben nur die Daten, die für die Funktionalität der Software notwendig sind.</li>
            <li><strong>Zweckbindung:</strong> Daten werden ausschließlich für die bereitgestellten Funktionen verwendet.</li>
            <li><strong>Transparenz:</strong> Sie haben jederzeit Einblick in Ihre gespeicherten Daten.</li>
            <li><strong>Löschung:</strong> Auf Wunsch werden alle Daten vollständig und unwiderruflich gelöscht.</li>
          </ul>

          <h3>Transparente Datenspeicherung</h3>
          <p>
            GarageDesk speichert alle Daten in einem transparenten, offenen Format:
          </p>
          <ul>
            <li><strong>JSON-Format:</strong> Alle Daten werden als JSON (JavaScript Object Notation) gespeichert - ein offener, 
            standardisierter und menschenlesbarer Datenaustauschformat.</li>
            <li><strong>Datenbankstruktur:</strong> Die Daten sind in einer Datenbank organisiert und strukturiert gespeichert, 
            nicht in einem proprietären oder verschlüsselten Format.</li>
            <li><strong>Keine Vendor-Lock-in:</strong> Da wir kein proprietäres Datenformat verwenden, sind Ihre Daten jederzeit 
            exportierbar und können mit Standard-Tools gelesen und verarbeitet werden.</li>
            <li><strong>Vollständige Nachvollziehbarkeit:</strong> Die Datenstruktur ist transparent und nachvollziehbar. 
            Sie können jederzeit sehen, welche Daten wie gespeichert werden.</li>
            <li><strong>Einfacher Export:</strong> Aufgrund des offenen Formats können Ihre Daten einfach exportiert und 
            in andere Systeme übertragen werden.</li>
          </ul>
          <p>
            Diese Transparenz gewährleistet, dass Sie die volle Kontrolle über Ihre Daten haben und nicht von einem 
            speziellen Datenformat abhängig sind.
          </p>

          <h3>Ihre Rechte</h3>
          <p>
            Sie haben jederzeit das Recht auf:
          </p>
          <ul>
            <li>Auskunft über Ihre gespeicherten Daten</li>
            <li>Berichtigung unrichtiger Daten</li>
            <li>Löschung Ihrer Daten</li>
            <li>Einschränkung der Verarbeitung</li>
            <li>Datenübertragbarkeit</li>
            <li>Widerspruch gegen die Verarbeitung</li>
          </ul>

          <h3>Compliance</h3>
          <p>
            GarageDesk ist DSGVO-konform entwickelt und erfüllt die Anforderungen der 
            Datenschutz-Grundverordnung (DSGVO). Wir arbeiten kontinuierlich daran, 
            die Sicherheit und den Datenschutz weiter zu verbessern.
          </p>

          <h3>Weitere Informationen</h3>
          <p>
            Detaillierte Informationen zum Datenschutz finden Sie in unserer 
            <Link to="/datenschutz"> Datenschutzerklärung</Link>. 
            Für rechtliche Fragen stehen Ihnen unsere 
            <Link to="/agbs"> Allgemeinen Geschäftsbedingungen</Link> zur Verfügung.
          </p>
        </section>

        <section className="doc-section">
          <h2>Support & Kontakt</h2>
          <p>
            Bei Fragen oder Problemen können Sie uns über das <Link to="/contact">Kontaktformular</Link> erreichen.
          </p>
          <p>
            Weitere Informationen finden Sie im <Link to="/impressum">Impressum</Link>.
          </p>
        </section>
      </div>
    </div>
  );
}

export default Documentation;

